/**
 * Client-side mapper for the PQR preview's Welding Parameters matrix
 * (slice 05). Each cell carries:
 *
 *   - `aggregate`: the value derived from `weldparams.*` (min/max), plus
 *     pipe-set `sections.preheat` for the `preheat` column (Root → preheat
 *     min/max, other passes → interpass min/max). This is informational — the
 *     editor shows the aggregate as a placeholder and as the source of the
 *     "Overridden — differs from per-weld telemetry" badge.
 *   - `override`: the value currently persisted at
 *     `sections.pqr.overrides.weldingParameters` for this (pass, column).
 *     `null` when no override is set.
 *
 * `value` is the user-facing display value (override if set, else aggregate).
 * The editor presents one min input and one max input per cell and persists
 * back into the override layer only.
 *
 * Everything in this module is pure — it never touches the DOM or the
 * network. The aggregation logic mirrors the server-side
 * `collectWeldParameterStats` so the client + server agree on the placeholder
 * shown to the user.
 */

import { filterDepositionPassLabels } from './floorSheetMisfirePrefill'
import { resolvePreheatCellsForPass } from './preheatInterpassForPass'

export type WeldingParameterColumnKey =
  | 'preheat'
  | 'amperage'
  | 'voltage'
  | 'wfs'
  | 'travel'
  | 'heatInput'

export const WELDING_PARAMETER_COLUMN_DEFS: Array<{
  key: WeldingParameterColumnKey
  header: string
  unit: string
}> = [
  { key: 'preheat', header: 'Preheat / Interpass', unit: '°F' },
  { key: 'amperage', header: 'Amperage', unit: 'A' },
  { key: 'voltage', header: 'Voltage', unit: 'V' },
  { key: 'wfs', header: 'Wire Feed Speed', unit: 'in/min' },
  { key: 'travel', header: 'Travel Speed', unit: 'in/min' },
  { key: 'heatInput', header: 'Heat Input', unit: 'kJ/in' }
]

type WeldRawKey = 'volts' | 'amps' | 'wireFeed' | 'travelSpeed' | 'heatInput'

const COLUMN_TO_RAW: Record<Exclude<WeldingParameterColumnKey, 'preheat'>, WeldRawKey> = {
  amperage: 'amps',
  voltage: 'volts',
  wfs: 'wireFeed',
  travel: 'travelSpeed',
  heatInput: 'heatInput'
}

export interface PqrEditableWeldingParameterCell {
  /** Aggregate min/max from `weldparams.*` (or the `preheat` section). */
  aggregate: { min: string; max: string }
  /** Override min/max persisted at `sections.pqr.overrides.weldingParameters`. */
  override: { min: string; max: string } | null
}

export interface PqrEditableWeldingParameterRow {
  passLabel: string
  cells: Record<WeldingParameterColumnKey, PqrEditableWeldingParameterCell>
}

export interface PqrEditableWeldingParameters {
  passes: string[]
  rows: PqrEditableWeldingParameterRow[]
}

// ---------------------------------------------------------------------------
// Pass expansion (kept in sync with pqrWeldingVariablesMapper)
// ---------------------------------------------------------------------------

const BASE_PASS_ORDER = ['Root', 'Hot', 'Fill', 'Cap', 'Strip', 'Root Misfire']
const MAX_FILL_COUNT = 21
const MAX_CAP_COUNT = 2

function clamp(n: number, max: number): number {
  return Math.min(max, Math.max(0, Math.trunc(n)))
}

function nonBlank(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== ''
}

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function resolveActivePassPayload(
  passSection: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!passSection) return null
  const bevelPasses = passSection.bevelPasses
  if (Array.isArray(bevelPasses) && bevelPasses.length > 0) {
    const rawIdx = passSection.activeBevelIndex
    const idx =
      typeof rawIdx === 'number' && Number.isFinite(rawIdx)
        ? Math.trunc(rawIdx)
        : typeof rawIdx === 'string' && rawIdx.trim() !== '' && Number.isFinite(Number(rawIdx))
          ? Math.trunc(Number(rawIdx))
          : 0
    const clamped = Math.min(bevelPasses.length - 1, Math.max(0, idx))
    return asObject(bevelPasses[clamped])
  }
  return passSection
}

function expandPassLabels(passSection: Record<string, unknown> | null): string[] {
  const payload = resolveActivePassPayload(passSection)
  if (!payload) return []
  const passSequence = Array.isArray(payload.passSequence)
    ? (payload.passSequence as unknown[]).filter(nonBlank)
    : []
  const selected = new Set(passSequence)
  const customPassesRaw = Array.isArray(payload.customPasses)
    ? (payload.customPasses as unknown[])
    : []
  const customPasses = customPassesRaw.map((item) => {
    if (typeof item === 'string') return { name: item, count: '1' }
    if (item && typeof item === 'object') {
      const obj = item as { name?: unknown; count?: unknown }
      return { name: String(obj.name ?? ''), count: String(obj.count ?? '1') }
    }
    return { name: '', count: '1' }
  })
  const order = [...BASE_PASS_ORDER]
  for (const p of customPasses) {
    if (p.name && !order.includes(p.name)) order.push(p.name)
  }
  const fillCount = clamp(Number(payload.fillCount ?? 0), MAX_FILL_COUNT)
  const capCount = clamp(Number(payload.capCount ?? 0), MAX_CAP_COUNT)
  const stripCount = Math.max(0, Math.trunc(Number(payload.stripCount ?? 0)))

  const list: string[] = []
  order.forEach((pass) => {
    if (!selected.has(pass)) return
    if (pass === 'Fill') {
      for (let i = 0; i < fillCount; i++) list.push(`Fill ${i + 1}`)
      return
    }
    if (pass === 'Cap') {
      for (let i = 0; i < capCount; i++) list.push(`Cap ${i + 1}`)
      return
    }
    if (pass === 'Strip') {
      for (let i = 0; i < stripCount; i++) list.push(`Strip ${i + 1}`)
      return
    }
    if (pass === 'Root Misfire') {
      const custom = customPasses.find((p) => p.name === pass)
      const count = Math.max(1, Math.trunc(Number(custom?.count || 1)))
      for (let i = 0; i < count; i++) list.push(`Root Misfire ${i + 1}`)
      return
    }
    const custom = customPasses.find((p) => p.name === pass)
    if (custom) {
      const count = Math.max(0, Math.trunc(Number(custom.count || 0)))
      for (let i = 0; i < count; i++) list.push(`${custom.name} ${i + 1}`)
      return
    }
    list.push(pass)
  })
  return list
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

interface MinMaxNumeric {
  min: number | null
  max: number | null
}

function readNumeric(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.trim())
    return Number.isFinite(n) ? n : null
  }
  return null
}

function combineNumeric(into: MinMaxNumeric, candidate: { min?: unknown; max?: unknown }): void {
  const min = readNumeric(candidate.min)
  const max = readNumeric(candidate.max)
  if (min !== null) into.min = into.min === null ? min : Math.min(into.min, min)
  if (max !== null) into.max = into.max === null ? max : Math.max(into.max, max)
}

function emptyAccum(): Record<WeldRawKey, MinMaxNumeric> {
  return {
    volts: { min: null, max: null },
    amps: { min: null, max: null },
    wireFeed: { min: null, max: null },
    travelSpeed: { min: null, max: null },
    heatInput: { min: null, max: null }
  }
}

function formatNumber(n: number | null, columnKey?: string): string {
  if (n === null) return ''
  if (columnKey === 'amperage' || columnKey === 'wfs') {
    return String(Math.round(n))
  }
  if (columnKey === 'travel') {
    return n.toFixed(1)
  }
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

function collectAggregates(
  sections: Record<string, unknown>
): Map<string, Record<WeldRawKey, MinMaxNumeric>> {
  const byPass = new Map<string, Record<WeldRawKey, MinMaxNumeric>>()
  const ensure = (pass: string) => {
    let row = byPass.get(pass)
    if (!row) {
      row = emptyAccum()
      byPass.set(pass, row)
    }
    return row
  }

  const readPassMappings = (wp: Record<string, unknown>): Record<string, string> | null => {
    const raw = wp.passMappings
    if (!raw || typeof raw !== 'object') return null
    const out: Record<string, string> = {}
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof value === 'string' && value.trim()) out[key] = value.trim()
    }
    return Object.keys(out).length > 0 ? out : null
  }

  const resolveAggregatePassKey = (
    row: Record<string, unknown>,
    passMappings: Record<string, string> | null
  ): string => {
    const pass = typeof row.pass === 'string' ? row.pass.trim() : ''
    if (!pass) return ''
    const wpsPass = typeof row.wpsPass === 'string' ? row.wpsPass.trim() : ''
    if (wpsPass) return wpsPass
    const mapped = passMappings?.[pass]
    if (mapped) return mapped
    return pass
  }

  for (const [key, raw] of Object.entries(sections)) {
    if (!key.startsWith('weldparams.')) continue
    const wp = asObject(raw)
    if (!wp) continue
    const passMappings = readPassMappings(wp)
    const rowsList: unknown[] = []
    if (Array.isArray(wp.rows)) rowsList.push(...wp.rows)
    const aggregate = asObject(wp.aggregate)
    if (aggregate && Array.isArray(aggregate.rows)) rowsList.push(...aggregate.rows)
    for (const rawRow of rowsList) {
      const row = asObject(rawRow)
      if (!row) continue
      const pass = resolveAggregatePassKey(row, passMappings)
      if (!pass) continue
      const values = asObject(row.values)
      if (!values) continue
      const accum = ensure(pass)
      ;(Object.values(COLUMN_TO_RAW) as WeldRawKey[]).forEach((rawKey) => {
        const cell = asObject(values[rawKey])
        if (cell) combineNumeric(accum[rawKey], cell)
      })
    }
  }
  return byPass
}

function readOverrides(
  sections: Record<string, unknown>
): Map<string, Partial<Record<WeldingParameterColumnKey, { min: string; max: string }>>> {
  const out = new Map<
    string,
    Partial<Record<WeldingParameterColumnKey, { min: string; max: string }>>
  >()
  const pqr = asObject(sections.pqr)
  const overridesRoot = asObject(pqr?.overrides)
  const list = overridesRoot?.weldingParameters
  if (!Array.isArray(list)) return out
  for (const raw of list) {
    const entry = asObject(raw)
    if (!entry) continue
    const passLabel = typeof entry.passLabel === 'string' ? entry.passLabel.trim() : ''
    if (!passLabel) continue
    const cellsRaw = asObject(entry.cells)
    if (!cellsRaw) continue
    const cells: Partial<Record<WeldingParameterColumnKey, { min: string; max: string }>> = {}
    ;(Object.keys(cellsRaw) as Array<keyof typeof cellsRaw>).forEach((colKey) => {
      if (!WELDING_PARAMETER_COLUMN_DEFS.some((d) => d.key === colKey)) return
      const cell = asObject(cellsRaw[colKey])
      if (!cell) return
      cells[colKey as WeldingParameterColumnKey] = {
        min: typeof cell.min === 'string' ? cell.min : '',
        max: typeof cell.max === 'string' ? cell.max : ''
      }
    })
    if (Object.keys(cells).length > 0) out.set(passLabel, cells)
  }
  return out
}

export function readWeldingParametersFromSections(
  sections: Record<string, unknown> | null | undefined
): PqrEditableWeldingParameters {
  const safeSections = asObject(sections) ?? {}
  const passSection = asObject(safeSections.pass)
  const passes = filterDepositionPassLabels(expandPassLabels(passSection))
  const aggregates = collectAggregates(safeSections)
  const preheatSection = asObject(safeSections.preheat)
  const overrides = readOverrides(safeSections)

  const rows: PqrEditableWeldingParameterRow[] = passes.map((passLabel) => {
    const passAgg = aggregates.get(passLabel)
    const passOverride = overrides.get(passLabel) ?? {}
    const cells = {} as Record<WeldingParameterColumnKey, PqrEditableWeldingParameterCell>
    WELDING_PARAMETER_COLUMN_DEFS.forEach((col) => {
      const aggregateCell =
        col.key === 'preheat'
          ? resolvePreheatCellsForPass(preheatSection, passLabel)
          : (() => {
              const rawKey = COLUMN_TO_RAW[col.key]
              const accum = passAgg?.[rawKey]
              return {
                min: accum ? formatNumber(accum.min, col.key) : '',
                max: accum ? formatNumber(accum.max, col.key) : ''
              }
            })()
      cells[col.key] = {
        aggregate: aggregateCell,
        override: passOverride[col.key] ?? null
      }
    })
    return { passLabel, cells }
  })

  return { passes, rows }
}
